import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import type { ReactNode } from 'react';
import type { AuthConfig } from '../api/types.ts';
import { chooseAccount, REMEMBERED_ACCOUNT } from './account-choice.ts';
import { safeReturnPath } from './return-path.ts';

/**
 * Where the user was when their session ran out, so sign-in can put them back.
 * Session-scoped: it is a breadcrumb for one redirect, not a preference.
 */
const RETURN_TO = 'procura.returnTo';
const DEV_TOKEN = 'procura.devToken';
/** Which local user the dev token is for, so a refusal can name it as Entra's does. */
const DEV_EMAIL = 'procura.devEmail';
/** This tab has already opened Microsoft's account picker by itself, so it does not loop on a cancel. */
const PICKER_OPENED = 'procura.pickerOpened';

// The account this browser chose (account-choice.ts). localStorage can throw
// (blocked storage); then nothing is remembered and the picker asks.
function readRemembered(): string | null {
  try { return localStorage.getItem(REMEMBERED_ACCOUNT); } catch { return null; }
}
function remember(homeAccountId: string): void {
  try { localStorage.setItem(REMEMBERED_ACCOUNT, homeAccountId); } catch { /* the picker will ask next time */ }
}
function forgetRemembered(): void {
  try { localStorage.removeItem(REMEMBERED_ACCOUNT); } catch { /* nothing to forget */ }
}

/**
 * The account the browser is signing in AS (19 October 2026): MSAL's active
 * account, or the local user. This is the account whose token the server
 * accepts or refuses, which can differ from the one a person believes they
 * used. On 19 October the browser quietly moved Procura to sys@, and the only
 * sign of it was "No staff account is linked to this sign-in", naming nobody.
 */
export interface SignedInAccount {
  /** The sign-in name: an email for every Allied account. */
  username: string;
}

export interface Session {
  config: AuthConfig;
  isSignedIn: boolean;
  /** Why the last session ended, if one did; null after a sign-in. */
  signInReason: SignInReason | null;
  /** The account being signed in as, or null when the browser holds none. */
  account: SignedInAccount | null;
  /** Resolves a usable access token, refreshing silently if it can. */
  getToken: () => Promise<string>;
  signIn: (devEmail?: string) => Promise<void>;
  /** Microsoft's account picker (prompt=select_account), back to this page; locally, the chooser. */
  signInWithDifferentAccount: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession outside SessionProvider');
  return ctx;
}

/**
 * The session ran out, told once, to the shell.
 *
 * `apiFetch` throws `NeedsSignIn` on a 401 with the stated intention that "the
 * shell redirects once, rather than each screen inventing its own handling".
 * Nothing ever caught it. So it arrived at whichever screen made the request as
 * an ordinary query error, and that screen showed both its own error *and*
 * `Staleness` reporting "Cannot reach the server" — two messages for one cause,
 * and neither of them the true one. An expired token is not a connectivity
 * failure and must not read as one.
 *
 * A module-level signal rather than context, because `api/client.ts` is not a
 * component and should not have to reach into React to say this.
 */
/**
 * Why the session ended, because the sign-in screen has to say which.
 *
 * `expired`: there was no token, or it could not be refreshed — ordinary.
 * `rejected`: the server refused a token the browser had. Signing in again
 * gets another token from the same place, and if the server refused the last
 * one it will likely refuse that too. Shown without a reason, that is a loop
 * that looks like the sign-in button not working, which is exactly how the
 * Azure audience fault presented for two sessions.
 */
export type SignInReason = 'expired' | 'rejected';

type SignInListener = (reason: SignInReason) => void;
const signInListeners = new Set<SignInListener>();

/** Called by apiFetch when the server rejects the token, and by getToken. */
export function announceSignInNeeded(reason: SignInReason = 'expired'): void {
  for (const listener of signInListeners) listener(reason);
}

function onSignInNeeded(listener: SignInListener): () => void {
  signInListeners.add(listener);
  return () => signInListeners.delete(listener);
}

export function rememberWhereWeWere(): void {
  sessionStorage.setItem(RETURN_TO, location.pathname + location.search);
}

export function whereWeWere(): string | null {
  const to = sessionStorage.getItem(RETURN_TO);
  if (to) sessionStorage.removeItem(RETURN_TO);
  // Only a same-site path: React Router 6's backslash open redirect has no 6.x fix (return-path.ts).
  return safeReturnPath(to);
}

/**
 * Sign-in, told by the server which kind it is.
 *
 * No session length is hardcoded anywhere in here, deliberately. Entra's
 * refresh token lifetime is tenant policy — 24 hours by default today, seven
 * days once Conditional Access can enforce it — and this code behaves
 * identically either way: refresh silently while the refresh token lasts, and
 * when it stops working, redirect and come back to the same page.
 */
export function SessionProvider(
  { config, children }: { config: AuthConfig; children: ReactNode },
) {
  const [isSignedIn, setSignedIn] = useState(
    () => config.mode === 'local' && !!localStorage.getItem(DEV_TOKEN),
  );
  // MSAL is loaded only in entra mode, so local development never downloads it.
  const msal = useRef<import('@azure/msal-browser').PublicClientApplication | null>(null);
  const [ready, setReady] = useState(config.mode === 'local');
  const [signInReason, setSignInReason] = useState<SignInReason | null>(null);

  useEffect(() => {
    if (config.mode !== 'entra') return;
    let cancelled = false;

    void (async () => {
      const { PublicClientApplication } = await import('@azure/msal-browser');
      const app = new PublicClientApplication({
        auth: {
          clientId: config.clientId,
          authority: config.authority,
          redirectUri: location.origin,
          navigateToLoginRequestUrl: false,
        },
        cache: {
          // Persisted, so a case manager is not signing in between visits and
          // a desktop user is not signing in all morning. The tablets are
          // single-assignment, PIN-locked and remotely wipeable; the device
          // lock is the control here, and a lost tablet is a wipe.
          cacheLocation: 'localStorage',
          storeAuthStateInCookie: false,
        },
      });
      await app.initialize();
      // A redirect that ended in an error (the picker cancelled, say) is not
      // a sign-in; it must not leave this screen at "Preparing sign-in…".
      const result = await app.handleRedirectPromise().catch(() => null);
      if (cancelled) return;

      // Never the first cached account (account-choice.ts, 19 October): the
      // one just chosen, else the one this browser remembers, else ask.
      const choice = chooseAccount(result?.account ?? null, app.getAllAccounts(), readRemembered());
      msal.current = app;
      if (choice.kind === 'use') {
        app.setActiveAccount(choice.account);
        if (choice.remember) remember(choice.account.homeAccountId);
        sessionStorage.removeItem(PICKER_OPENED);
      } else {
        app.setActiveAccount(null);
        // Accounts are cached but none is this browser's: Microsoft's picker,
        // once per tab. If it comes back with nothing (cancelled), the sign-in
        // screen, whose button opens the picker again.
        if (choice.kind === 'pick' && !sessionStorage.getItem(PICKER_OPENED)) {
          sessionStorage.setItem(PICKER_OPENED, '1');
          rememberWhereWeWere();
          await app.loginRedirect({ scopes: config.scopes, prompt: 'select_account' });
          return;
        }
      }

      setSignedIn(!!app.getActiveAccount());
      setReady(true);
    })();

    return () => { cancelled = true; };
  }, [config]);

  /**
   * One place decides that the session is over.
   *
   * Remember where we were first — the user was mid-task, and coming back to
   * the client list when they were on a client is its own small failure.
   */
  useEffect(() =>
    onSignInNeeded((reason) => {
      setSignInReason(reason);
      setSignedIn((wasSignedIn) => {
        if (wasSignedIn) rememberWhereWeWere();
        return false;
      });
      if (config.mode === 'local') { localStorage.removeItem(DEV_TOKEN); localStorage.removeItem(DEV_EMAIL); }
    }),
  [config.mode]);

  const signIn = useCallback(async (devEmail?: string) => {
    if (config.mode === 'local') {
      if (!devEmail) throw new Error('Choose a user to sign in as.');
      const res = await fetch('/api/dev/token', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ email: devEmail }),
      });
      if (!res.ok) throw new Error(`Could not mint a token for ${devEmail}`);
      const { token } = (await res.json()) as { token: string };
      localStorage.setItem(DEV_TOKEN, token);
      localStorage.setItem(DEV_EMAIL, devEmail);
      setSignInReason(null);
      setSignedIn(true);
      return;
    }
    // The sign-in screen is reached only with no account this browser has
    // chosen, so it always asks which (account-choice.ts).
    rememberWhereWeWere();
    await msal.current?.loginRedirect({ scopes: config.scopes, prompt: 'select_account' });
  }, [config]);

  const signOut = useCallback(async () => {
    if (config.mode === 'local') {
      localStorage.removeItem(DEV_TOKEN);
      localStorage.removeItem(DEV_EMAIL);
      setSignedIn(false);
      return;
    }
    // Signed out, so this browser no longer has a chosen account: the next
    // sign-in asks which.
    forgetRemembered();
    await msal.current?.logoutRedirect();
  }, [config]);

  // Microsoft's account picker, never a silent reuse of the account the server
  // just refused. Back to this page afterwards. Locally there is no picker: the
  // chooser is the sign-in screen, so this signs the local user out.
  const signInWithDifferentAccount = useCallback(async () => {
    if (config.mode === 'local') {
      localStorage.removeItem(DEV_TOKEN);
      localStorage.removeItem(DEV_EMAIL);
      setSignedIn(false);
      return;
    }
    rememberWhereWeWere();
    await msal.current?.loginRedirect({ scopes: config.scopes, prompt: 'select_account' });
  }, [config]);

  const account = useMemo<SignedInAccount | null>(() => {
    if (!isSignedIn) return null;
    if (config.mode === 'local') {
      const email = localStorage.getItem(DEV_EMAIL);
      return email ? { username: email } : null;
    }
    const active = msal.current?.getActiveAccount();
    return active?.username ? { username: active.username } : null;
  }, [config.mode, isSignedIn]);

  const getToken = useCallback(async (): Promise<string> => {
    if (config.mode === 'local') {
      const token = localStorage.getItem(DEV_TOKEN);
      // Announced, not merely thrown — otherwise this surfaces on whichever
      // screen asked as an ordinary query error, beside a Staleness line
      // claiming the server is unreachable.
      if (!token) { announceSignInNeeded(); throw new NeedsSignIn(); }
      return token;
    }

    const app = msal.current;
    const account = app?.getActiveAccount();
    if (!app || !account) { announceSignInNeeded(); throw new NeedsSignIn(); }

    try {
      const { accessToken } = await app.acquireTokenSilent({
        scopes: config.scopes,
        account,
      });
      return accessToken;
    } catch {
      // Silent refresh has run out — the refresh token expired, or the
      // tenant now wants the user to sign in again. Neither is an error
      // worth showing; it is a redirect, and we come back to this page.
      rememberWhereWeWere();
      await app.acquireTokenRedirect({ scopes: config.scopes, account });
      announceSignInNeeded();
      throw new NeedsSignIn();
    }
  }, [config]);

  const value = useMemo<Session>(
    () => ({ config, isSignedIn, signInReason, account, getToken, signIn, signInWithDifferentAccount, signOut }),
    [config, isSignedIn, signInReason, account, getToken, signIn, signInWithDifferentAccount, signOut],
  );

  if (!ready) return <Splash>Preparing sign-in…</Splash>;
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export class NeedsSignIn extends Error {
  constructor() {
    super('Sign-in required');
    this.name = 'NeedsSignIn';
  }
}

export function Splash({ children }: { children: ReactNode }) {
  return (
    <div className="flex min-h-dvh items-center justify-center p-6 text-neutral-500">
      {children}
    </div>
  );
}
